/**
 * PresenterOutput — the popup/window that gets projected. Runs in one
 * of three variants driven by the URL param `:mode`:
 *   • audience   — full-screen clean slide (projector / stream)
 *   • stage      — current slide + next slide + block notes + timer
 *   • confidence — current slide + upcoming block list + big clock
 *
 * State is received exclusively via the BroadcastChannel from the
 * director console. This page loads the initial payload itself so it can
 * render if opened before the director sends any state.
 *
 * Route: /worship/output/:serviceId/:mode
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { getToken } from '@/lib/authToken';
import { usePresenterChannel } from './usePresenterChannel';
import SlideCanvas from './SlideCanvas';
import CaptionsOverlay from './CaptionsOverlay';
import { DEFAULT_THEME } from './backgrounds';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

export default function PresenterOutput() {
  const { serviceId, mode = 'audience' } = useParams();
  const [payload, setPayload] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const [blackout, setBlackout] = useState(false);
  const [freeze, setFreeze] = useState(false);
  const [themeOverride, setThemeOverride] = useState(null);
  const [chordMode, setChordMode] = useState(false);
  const [transpose, setTranspose] = useState(0);
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [captionOutputs, setCaptionOutputs] = useState({ audience: true, stage: true, confidence: true });
  const [captions, setCaptions] = useState([]);       // { text, at, translations? }
  const [captionLanguages, setCaptionLanguages] = useState([]);
  // Read `?lang=xx` from URL — sticks unless director-side languages change
  const [displayLang, setDisplayLang] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('lang') || 'en'; }
    catch (e) { return 'en'; }
  });
  const [now, setNow] = useState(new Date());

  const { post } = usePresenterChannel(serviceId, {
    role: 'output',
    onMessage: (msg) => {
      if (!msg) return;
      if (msg.kind === 'state') {
        if (typeof msg.slideIdx === 'number') setSlideIdx(msg.slideIdx);
        if (typeof msg.blackout === 'boolean') setBlackout(msg.blackout);
        if (typeof msg.freeze === 'boolean') setFreeze(msg.freeze);
        if (msg.theme) setThemeOverride(msg.theme);
        if (typeof msg.chordMode === 'boolean') setChordMode(msg.chordMode);
        if (typeof msg.transpose === 'number') setTranspose(msg.transpose);
        if (typeof msg.captionsEnabled === 'boolean') setCaptionsEnabled(msg.captionsEnabled);
        if (msg.captionOutputs) setCaptionOutputs(msg.captionOutputs);
        if (Array.isArray(msg.captionLanguages)) setCaptionLanguages(msg.captionLanguages);
      } else if (msg.kind === 'caption' && msg.text) {
        setCaptions((prev) => {
          const next = [...prev, { text: msg.text, at: msg.at || Date.now(), translations: {} }];
          return next.length > 15 ? next.slice(-15) : next;
        });
      } else if (msg.kind === 'caption-translations' && msg.at) {
        setCaptions((prev) => prev.map(
          (c) => (c.at === msg.at ? { ...c, translations: { ...(c.translations || {}), ...(msg.translations || {}) } } : c),
        ));
      }
    },
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/worship/services/${serviceId}/present`, { headers: authH() });
        if (!cancelled) setPayload(r.data);
      } catch (e) { /* handled in UI */ }
    })();
    return () => { cancelled = true; };
  }, [serviceId]);

  // Ask the director for the latest state on mount so we catch up
  useEffect(() => { post({ kind: 'sync?' }); }, [post]);

  // Clock tick for stage/confidence
  useEffect(() => {
    if (mode === 'audience') return undefined;
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [mode]);

  const slides = payload?.slides || [];
  const theme = themeOverride || payload?.theme || DEFAULT_THEME;
  const currentIdx = Math.max(0, Math.min(slideIdx, slides.length - 1));
  const current = slides[currentIdx];
  const next = slides[currentIdx + 1];

  useEffect(() => {
    document.title = payload?.service?.name
      ? `${payload.service.name} — ${mode}`
      : `Worship — ${mode}`;
    document.body.style.margin = '0';
    document.body.style.background = '#000';
  }, [payload, mode]);

  // Transform caption buffer for the currently-chosen display language.
  // Must live before any early returns (React rules-of-hooks).
  const displayCaptions = useMemo(() => (
    (captions || []).map((c) => ({
      ...c,
      text: displayLang !== 'en' && c.translations?.[displayLang]
        ? c.translations[displayLang]
        : c.text,
      pending: displayLang !== 'en' && !c.translations?.[displayLang],
    }))
  ), [captions, displayLang]);

  if (!payload) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-slate-950 text-slate-400" data-testid="output-loading">
        Loading service…
      </div>
    );
  }

  if (blackout) {
    return <div className="h-screen w-screen bg-black" data-testid="output-blackout" />;
  }

  const canvas = (
    <SlideCanvas
      slide={current}
      theme={theme}
      variant={mode}
      chordMode={chordMode}
      transposeSemitones={transpose}
    />
  );

  const showCaptionsHere = captionsEnabled && captionOutputs?.[mode] !== false;
  const captionOverlay = showCaptionsHere && !blackout ? (
    <CaptionsOverlay captions={displayCaptions} variant={mode} lineLimit={2} />
  ) : null;

  const langChip = showCaptionsHere && captionLanguages.length > 0 ? (
    <LanguageChip
      current={displayLang}
      options={['en', ...captionLanguages]}
      onChange={setDisplayLang}
    />
  ) : null;

  if (mode === 'audience') {
    return (
      <div className="relative h-screen w-screen" data-testid="output-audience">
        {canvas}
        {captionOverlay}
        {langChip}
      </div>
    );
  }

  if (mode === 'stage') {
    return (
      <StageChrome
        canvas={canvas}
        captionOverlay={captionOverlay}
        langChip={langChip}
        current={current}
        next={next}
        slidesTotal={slides.length}
        currentIdx={currentIdx}
        service={payload.service}
        now={now}
      />
    );
  }

  return (
    <ConfidenceChrome
      canvas={canvas}
      captionOverlay={captionOverlay}
      langChip={langChip}
      current={current}
      slides={slides}
      currentIdx={currentIdx}
      service={payload.service}
      now={now}
    />
  );
}


function LanguageChip({ current, options, onChange }) {
  return (
    <div className="absolute top-3 right-3 z-40 pointer-events-auto" data-testid="lang-chip">
      <select
        value={current}
        onChange={(e) => onChange(e.target.value)}
        data-testid="lang-chip-select"
        className="rounded border border-slate-700 bg-black/70 px-1.5 py-0.5 text-[10px] text-slate-100 uppercase tracking-wider"
      >
        {options.map((code) => <option key={code} value={code}>{code}</option>)}
      </select>
    </div>
  );
}

function timeStr(d) {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function StageChrome({ canvas, captionOverlay, langChip, current, next, slidesTotal, currentIdx, service, now }) {
  return (
    <div className="grid h-screen w-screen grid-rows-[1fr_240px] bg-slate-950 text-slate-100" data-testid="output-stage">
      <div className="relative min-h-0">
        {canvas}
        {captionOverlay}
        {langChip}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 border-t border-slate-800 bg-slate-950/95 p-3 text-sm">
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-300">Up Next</div>
          {next ? (
            <>
              <div className="mt-1 font-semibold text-slate-100 line-clamp-1">{next.title || next.type}</div>
              <div className="text-xs text-slate-400 line-clamp-3">
                {next.body || next.scripture?.ref || next.song?.title || '—'}
              </div>
            </>
          ) : (
            <p className="mt-2 text-xs text-slate-500">End of service</p>
          )}
        </div>
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
          <div className="text-[10px] uppercase tracking-wider text-amber-300">Notes</div>
          <p className="mt-1 text-xs text-slate-300 line-clamp-6 whitespace-pre-wrap">
            {current?.notes || '—'}
          </p>
        </div>
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3 text-right">
          <div className="text-[10px] uppercase tracking-wider text-sky-300">Clock</div>
          <div className="mt-1 text-3xl font-bold tabular-nums text-slate-50">{timeStr(now)}</div>
          <div className="mt-2 text-[10px] text-slate-500">
            {service?.name} · {currentIdx + 1} / {slidesTotal}
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfidenceChrome({ canvas, captionOverlay, langChip, current, slides, currentIdx, service, now }) {
  const remaining = useMemo(() => slides.slice(currentIdx + 1, currentIdx + 6), [slides, currentIdx]);
  return (
    <div className="grid h-screen w-screen grid-cols-[1fr_340px] bg-slate-950 text-slate-100" data-testid="output-confidence">
      <div className="relative min-h-0">
        {canvas}
        {captionOverlay}
        {langChip}
      </div>
      <div className="flex flex-col gap-3 border-l border-slate-800 bg-slate-950/95 p-3">
        <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-3 text-right">
          <div className="text-[10px] uppercase tracking-wider text-sky-300">Clock</div>
          <div className="text-4xl font-bold tabular-nums text-slate-50">{timeStr(now)}</div>
          <div className="mt-1 text-[10px] text-slate-500">{service?.name}</div>
        </div>
        <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-300">Now</div>
          <div className="mt-1 font-semibold text-slate-100">
            {current?.title || current?.type}
          </div>
          <p className="text-xs text-slate-400">
            slide {currentIdx + 1} / {slides.length}
          </p>
        </div>
        <div className="min-h-0 flex-1 rounded-lg border border-slate-800 bg-slate-900/70 p-3">
          <div className="text-[10px] uppercase tracking-wider text-violet-300">Upcoming</div>
          <ul className="mt-1 space-y-1 overflow-y-auto text-xs" style={{ maxHeight: 'calc(100vh - 260px)' }}>
            {remaining.length === 0 && <li className="text-slate-500">End of service</li>}
            {remaining.map((s, i) => (
              <li key={s.index} className="rounded border border-slate-800 bg-slate-950 px-2 py-1">
                <span className="text-slate-500 mr-2">{currentIdx + 2 + i}.</span>
                <span className="text-slate-200">{s.title || s.type}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
